import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Scan, Languages, Calendar, MessageCircle, CheckCircle2, Plus, Minus, Sparkles, Smile, Palette } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-4349">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-4350" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-4351">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-4352">
        <span className="text-lg font-medium text-gray-900 group-hover:text-forest-700 transition-colors" data-id="element-4353">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-forest-600 text-white' : 'bg-stone-100 text-gray-400'}`} data-id="element-4354">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-4355" /> : <Plus className="w-4 h-4" data-id="element-4356" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-4357">
        <p className="text-gray-600 leading-relaxed" data-id="element-4358">{answer}</p>
      </div>
    </div>;
}
export function VeneersLondonRuPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Сколько стоят виниры в Лондоне?',
    a: 'Цена зависит от материала (керамика или композит), количества зубов и сложности работы. После консультации мы предлагаем детальный план лечения и смету.'
  }, {
    q: 'Нужно ли обтачивать зубы под виниры?',
    a: 'В большинстве случаев требуется минимальная подготовка эмали (0.3-0.5 мм), но объем зависит от клинической ситуации. Мы всегда выбираем максимально щадящий подход (minimally invasive).'
  }, {
    q: 'Сколько служат виниры?',
    a: 'Керамические виниры могут служить 10-15 лет и более при правильном уходе. Срок службы зависит от материала, прикуса и гигиены. На консультации мы объясняем, как продлить долговечность.'
  }, {
    q: 'Можно ли сделать естественный результат?',
    a: 'Да, это наша специализация. Мы подбираем форму, текстуру и цвет индивидуально, чтобы улыбка выглядела гармонично и натурально, а не искусственно.'
  }, {
    q: 'Какая разница между фарфоровыми и композитными винирами?',
    a: 'Фарфоровые (керамические) виниры прочнее, долговечнее и устойчивее к окрашиванию. Композитные виниры (bonding) доступнее по цене и часто устанавливаются за один визит, но требуют более частой полировки.'
  }, {
    q: 'Сколько визитов нужно для виниров?',
    a: 'Обычно 2-3 визита для керамических виниров: консультация, подготовка/оттиски, установка. Композитные виниры часто можно сделать за один визит.'
  }, {
    q: 'Можно ли получить консультацию на русском?',
    a: 'Да, в Dentico Clinic возможно общение на русском языке во время консультации и лечения.'
  }, {
    q: 'Где находится Dentico Clinic?',
    a: 'Angel / Islington, 360–364 City Road, London EC1V 2PY. 5 минут пешком от станции Angel.'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Dental Veneers',
    description: 'Cosmetic dental procedure to improve the appearance of teeth using veneers.',
    procedureType: 'http://schema.org/NoninvasiveProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-4359">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-4360" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-4361" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-4362" />

      {/* 1. HERO SECTION */}
      <section className="relative bg-[#F8F5F0] overflow-hidden" data-id="element-4363">
        <div className="absolute inset-0 bg-[#EFE8DE] -skew-y-3 origin-top-left transform translate-y-20" data-id="element-4364" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-4365">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-4366">
            <div className="space-y-6" data-id="element-4367">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EFE8DE] text-[#2F4A3F] border border-[#D8C4A8] text-sm font-medium" data-id="element-4368">
                <span className="relative flex h-2 w-2" data-id="element-4369">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-forest-600 opacity-75" data-id="element-4370"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-forest-700" data-id="element-4371"></span>
                </span>
                Эстетическая стоматология
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2B2D2F] leading-tight tracking-tight" data-id="element-4372">
                Виниры в Лондоне –{' '}
                <span className="text-forest-700" data-id="element-4373">Dentico Clinic</span>
                <br data-id="element-4374" />
                <span className="text-2xl sm:text-3xl font-medium text-stone-500 mt-2 block" data-id="element-4375">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-4376">
                Создайте идеальную улыбку с помощью керамических виниров или
                композитного бондинга. Естественный результат, минимальное
                вмешательство, консультация на русском.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-[#2F4A3F]" data-id="element-4377">
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-4378">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" data-id="element-4379" />
                  Smile Makeover
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-4380">
                  <MapPin className="w-4 h-4 text-[#2F4A3F]" data-id="element-4381" />
                  Angel, EC1V
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-4382">
                  <Languages className="w-4 h-4 text-[#2F4A3F]" data-id="element-4383" />
                  Русский язык
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-4384">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-4385">
                  <Button size="lg" className="w-full gap-2 !opacity-100 !bg-[#2F4A3F] !text-white !border !border-[#2F4A3F] hover:!bg-[#3A5A4D]" data-id="element-4386">
                    <Calendar className="w-5 h-5" data-id="element-4387" />
                    Записаться на консультацию
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-4388">
                  <Button size="lg" className="w-full gap-2 !opacity-100 !bg-[#EFE8DE] !text-[#2F4A3F] !border !border-[#D8C4A8] hover:!bg-[#2F4A3F] hover:!text-white" data-id="element-4389">
                    <MessageCircle className="w-5 h-5" data-id="element-4390" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-4391">
              <div className="absolute inset-0 bg-[#D8C4A8] rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-4392"></div>
              <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Виниры и эстетическая стоматология - Dentico Clinic" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full" data-id="element-4393" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHAT ARE VENEERS */}
      <section className="py-16 bg-white" data-id="element-4394">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4395">
          <div className="text-center mb-10" data-id="element-4396">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-4397">
              Что такое виниры?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-4398">
              Виниры — это ультратонкие керамические накладки, которые
              фиксируются на переднюю поверхность зубов для улучшения их формы,
              цвета и положения. Это идеальное решение для коррекции сколов,
              промежутков между зубами (диастем), изменения цвета эмали, который
              не поддается отбеливанию, или выравнивания незначительных
              неровностей.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mt-4" data-id="element-4399">
              Современные виниры выглядят абсолютно естественно благодаря
              способности пропускать свет так же, как натуральная эмаль. В
              Dentico Clinic мы используем цифровые технологии для моделирования
              вашей будущей улыбки еще до начала лечения.
            </p>
          </div>
        </div>
      </section>

      {/* 3. PROCESS */}
      <section className="py-16 bg-gray-50" data-id="element-4400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4401">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-4402">
            Путь к новой улыбке
          </h2>
          <div className="grid md:grid-cols-4 gap-8" data-id="element-4403">
            {[{
            step: '01',
            title: 'Консультация',
            desc: 'Обсуждение пожеланий, фотопротокол и цифровое моделирование улыбки.'
          }, {
            step: '02',
            title: 'Планирование',
            desc: 'Примерка макета (mock-up), чтобы вы увидели результат до начала работ.'
          }, {
            step: '03',
            title: 'Подготовка',
            desc: 'Минимальная обработка зубов и снятие точных оттисков или сканирование.'
          }, {
            step: '04',
            title: 'Фиксация',
            desc: 'Установка готовых керамических виниров и финальная эстетика.'
          }].map((item, idx) => <Card key={idx} className="p-6 relative overflow-hidden" data-id="element-4404">
                <div className="text-6xl font-bold text-[#EFE8DE] absolute -top-4 -right-4 select-none" data-id="element-4405">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-4406">
                  <h3 className="text-xl font-bold text-[#2B2D2F] mb-3" data-id="element-4407">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-4408">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 4. TYPES */}
      <section className="py-16 bg-white" data-id="element-4409">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4410">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-4411">
            <div data-id="element-4412">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-4413">
                Виды эстетической реставрации
              </h2>
              <div className="space-y-6" data-id="element-4414">
                <div className="flex gap-4" data-id="element-4415">
                  <div className="p-3 bg-[#F8F5F0] border border-[#D8C4A8] rounded-lg text-[#2F4A3F] h-fit" data-id="element-4416">
                    <Sparkles className="w-6 h-6" data-id="element-4417" />
                  </div>
                  <div data-id="element-4418">
                    <h3 className="text-xl font-bold text-[#2B2D2F] mb-2" data-id="element-4419">
                      Керамические виниры
                    </h3>
                    <p className="text-gray-600" data-id="element-4420">
                      Изготавливаются в лаборатории из высокопрочной керамики.
                      Максимально эстетичные, долговечные и устойчивые к
                      изменению цвета.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-4421">
                  <div className="p-3 bg-[#F8F5F0] border border-[#D8C4A8] rounded-lg text-[#2F4A3F] h-fit" data-id="element-4422">
                    <Palette className="w-6 h-6" data-id="element-4423" />
                  </div>
                  <div data-id="element-4424">
                    <h3 className="text-xl font-bold text-[#2B2D2F] mb-2" data-id="element-4425">
                      Композитный бондинг
                    </h3>
                    <p className="text-gray-600" data-id="element-4426">
                      Художественная реставрация композитным материалом прямо в
                      кресле. Быстрее и доступнее, но требует периодической
                      полировки.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-4427">
                  <div className="p-3 bg-[#F8F5F0] border border-[#D8C4A8] rounded-lg text-[#2F4A3F] h-fit" data-id="element-4428">
                    <Smile className="w-6 h-6" data-id="element-4429" />
                  </div>
                  <div data-id="element-4430">
                    <h3 className="text-xl font-bold text-[#2B2D2F] mb-2" data-id="element-4431">
                      Smile Makeover
                    </h3>
                    <p className="text-gray-600" data-id="element-4432">
                      Комплексное преображение улыбки, которое может сочетать
                      виниры, коронки и отбеливание для идеального результата.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl" data-id="element-4433">
              <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Типы виниров" className="absolute inset-0 w-full h-full object-cover" data-id="element-4434" />
            </div>
          </div>
        </div>
      </section>

      {/* 5. BENEFITS */}
      <section className="py-16 bg-[#EFE8DE]" data-id="element-4435">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4436">
          <div className="text-center mb-12" data-id="element-4437">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-4438">
              Почему стоит выбрать Dentico Clinic
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-4439">
            {[{
            title: 'Естественный вид',
            desc: 'Мы создаем улыбки, которые выглядят натурально, а не искусственно.'
          }, {
            title: 'Минимальное вмешательство',
            desc: 'Сохраняем максимум здоровых тканей зуба благодаря современным техникам.'
          }, {
            title: 'Русский язык',
            desc: 'Легкое общение и полное понимание ваших пожеланий.'
          }, {
            title: 'Удобная локация',
            desc: 'Клиника в центре Лондона (Angel), легко добраться из любого района.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white text-center" data-id="element-4440">
                <div className="w-12 h-12 bg-[#F8F5F0] rounded-full flex items-center justify-center text-[#2F4A3F] mb-4 mx-auto border border-[#D8C4A8]" data-id="element-4441">
                  <CheckCircle2 className="w-6 h-6" data-id="element-4442" />
                </div>
                <h3 className="text-lg font-bold text-[#2B2D2F] mb-2" data-id="element-4443">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-4444">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-4445">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4446">
          <div className="text-center mb-10" data-id="element-4447">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-4448">
              Частые вопросы о винирах
            </h2>
          </div>
          <div className="space-y-2" data-id="element-4449">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-4450" />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-4451">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4452">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-4453">
            Смотрите также
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-4454">
            <Link to="/ru/russkij-stomatolog-london" data-id="element-4455">
              <Button variant="outline" size="sm" data-id="element-4456">
                Русскоязычный стоматолог
              </Button>
            </Link>
            <Link to="/ru/services/smile-makeover" data-id="element-4457">
              <Button variant="outline" size="sm" data-id="element-4458">
                Smile Makeover
              </Button>
            </Link>
            <Link to="/ru/services/whitening" data-id="element-4459">
              <Button variant="outline" size="sm" data-id="element-4460">
                Отбеливание
              </Button>
            </Link>
            <Link to="/ru/prices" data-id="element-4461">
              <Button variant="outline" size="sm" data-id="element-4462">
                Цены
              </Button>
            </Link>
            <Link to="/ru/contact" data-id="element-4463">
              <Button variant="outline" size="sm" data-id="element-4464">
                Контакты
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA */}
      <section className="py-16 bg-forest-900" data-id="element-4465">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-4466">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-4467">
            Мечтаете об идеальной улыбке?
          </h2>
          <p className="text-xl text-ivory/80 mb-8 max-w-2xl mx-auto" data-id="element-4468">
            Запишитесь на консультацию и узнайте, как виниры могут изменить вашу
            жизнь.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-4469">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-4470">
              <Button variant="primary" surface="dark" size="lg" className="w-full gap-2" data-id="element-4471">
                <Calendar className="w-5 h-5" data-id="element-4472" />
                Записаться онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-4473">
              <Button variant="whatsapp" surface="dark" size="lg" className="w-full gap-2" data-id="element-4474">
                <MessageCircle className="w-5 h-5" data-id="element-4475" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>;
}